import { isGroupMissing, TeamsLinked } from '../../utils/teamGroupRule';

/** A field of Bulk Edit: ticked (`required`) means it is set on every selected ticket. */
interface BulkField {
  value?: unknown;
  required?: boolean;
}

/** Bulk Edit's state, as far as its rule reads it - every other key is a field like these. */
export interface BulkEditState {
  TeamId?: BulkField;
  SupportGroupId?: BulkField;
  CustomPropertiesAnswers?: { key: string; editValue?: BulkField }[];
}

/** The key a custom property's message is filed under, beside the ticket fields' own names. */
export const customPropertyKey = (propertyId: string) => `cp:${propertyId}`;

/**
 * No value: nothing picked, an empty list, or 0 (what a cleared Assignee holds). "No group" (-1) and "Unassigned" (-1)
 * are choices, so they count.
 */
const isEmpty = (value: unknown): boolean =>
  value == null || value === '' || value === 0 || (Array.isArray(value) && value.length === 0);

/** Every ticked field, by key, and whether it has a value. */
const tickedFields = (data: BulkEditState): { key: string; empty: boolean }[] => {
  if (!data) return [];
  const ticked = Object.entries(data)
    .filter(([key]) => key !== 'CustomPropertiesAnswers')
    .map(([key, field]: [string, BulkField]) => ({ key, field }))
    .filter(({ field }) => !!field?.required)
    .map(({ key, field }) => ({ key, empty: isEmpty(field.value) }));
  for (const property of data.CustomPropertiesAnswers ?? [])
    if (property.editValue?.required) ticked.push({ key: customPropertyKey(property.key), empty: isEmpty(property.editValue.value) });
  return ticked;
};

/** The ticked fields with no value - they hold Update back. */
export const bulkEditEmptyFields = (data: BulkEditState): string[] =>
  tickedFields(data).filter(field => field.empty).map(field => field.key);

/**
 * The ticked fields holding a value. A ticked field says it needs one only after it has had one and been emptied
 * (Shaun, 2026-10-20) - the form remembers these until the box is unticked.
 */
export const bulkEditFilledFields = (data: BulkEditState): string[] =>
  tickedFields(data).filter(field => !field.empty).map(field => field.key);

/** Whether anything is ticked at all. */
const anythingTicked = (data: BulkEditState): boolean =>
  !!data && (Object.keys(data).some(key => key !== 'CustomPropertiesAnswers' && !!((data as Record<string, unknown>)[key] as BulkField)?.required)
  || (data.CustomPropertiesAnswers ?? []).some(property => !!property.editValue?.required));

/**
 * Update stays off until something is ticked and every ticked field has a value (Shaun, 2026-10-20) - and, as before, a
 * team with groups is given one.
 */
export const bulkEditBlocked = (data: BulkEditState, teams: TeamsLinked[], groups: TeamsLinked[]): boolean =>
  !anythingTicked(data)
  || bulkEditEmptyFields(data).length > 0
  || (!!data?.TeamId?.required && isGroupMissing(data.TeamId.value as number, data.SupportGroupId?.value as number, teams, groups));
